import React, { useContext, useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useDebounce } from '@node-space/hooks'
import {
  Button,
  EmptyState,
  Pagination,
  RowsPerPageType,
  RowsSkeletonLoader,
  toast,
  Box as Wrapper,
} from '@node-space/ui-components'
import { logSentryErrorAndGetEventId } from '@node-space/utils'
import { Lottie } from 'components/Animations/ThemeLottie'
import { ContactsSearchListLoader } from 'components/Contacts/ContactsSearchListLoader'
import { ExternalLink } from 'components/ExternalLink/ExternalLink'
import { rootInitialState } from 'components/FilterBar/context/reducer'
import useFilterMerchant from 'components/FilterBar/hooks/useFilterMerchant'
import useFilterSearch from 'components/FilterBar/hooks/useFilterSearch'
import { PageHeader } from 'components/header/PageHeader'
import { SEARCH_INPUT_DEBOUNCE_TIME } from 'constants/General'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useUpdateChannel } from 'hooks/mutations/useUpdateChannel'
import { useChannelsQuery, useSupportedCurrenciesQuery } from 'hooks/queries'
import { ChannelsHeaderButtons } from 'pages/Channels/components/ChannelsHeaderButtons'
import { contactActionAtom, contactAtom } from 'pages/Contacts/atoms/contactAtoms'
import { ContactsSheets } from 'pages/Contacts/components/ContactsSheets'
import { getRequiredContactFieldsForCryptoAddress } from 'pages/Contacts/utils/fiatInstruments'
import { getContactComplianceDetails } from 'pages/Contacts/utils/utils'
import { WalletSheet } from 'pages/Wallets/components/Sheet/WalletSheet'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { ContactActionIds } from 'types/atoms/atom.contacts'
import { BaseErrorResponse } from 'types/beneficiaries'
import { Channel, UpdateChannel } from 'types/channels'
import { Contact, InstrumentType } from 'types/contacts'
import { PartyDetailsType } from 'types/payments'
import { ActionIds } from 'types/types'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import { ChannelDelete } from './components/ChannelDelete'
import { ChannelsFilterBar } from './components/ChannelsFilterBar'
import ChannelsTable from './components/ChannelsTable'

const ManageChannels = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [, setWalletAction] = useContext(WalletActionContext)
  const { merchant, setMerchant, resetMerchant } = useFilterMerchant()
  const { search, setSearch, resetSearch, resetIsSearchUsed } = useFilterSearch()

  const setContactAction = useSetAtom(contactActionAtom)
  const setContact = useSetAtom(contactAtom)
  const [isContactSheetOpen, setIsContactSheetOpen] = useState(false)
  const [selectedChannelToDelete, setSelectedChannelToDelete] = useState<Channel | null>(null)

  const { data: currencies, isPending: isCurrenciesLoading } = useSupportedCurrenciesQuery()
  const { mutate, isPending: isPendingUpdateChannel } = useUpdateChannel()

  const debouncedSearch = useDebounce(search, SEARCH_INPUT_DEBOUNCE_TIME)

  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<RowsPerPageType>(10)
  const [offset, setOffset] = useState(0)

  const {
    data: { channels = [], paging } = {},
    isFetching: isChannelsFetching,
    isError: isChannelsError,
    refetch: refetchChannels,
  } = useChannelsQuery({
    max: Number(rowsPerPage),
    offset,
    merchant,
    search: debouncedSearch,
  })

  useEffect(() => {
    refetchChannels()
  }, [debouncedSearch, offset, rowsPerPage, merchant])

  useEffect(() => {
    if (search == rootInitialState?.search && merchant == rootInitialState?.merchant) {
      refetchChannels()
    }
    setOffset(0)
  }, [search, merchant])

  useEffect(() => {
    return () => {
      resetSearch()
      resetMerchant()
    }
  }, [])

  const onResetAll = () => {
    setMerchant(rootInitialState?.merchant)
    setSearch(rootInitialState?.search)
    resetIsSearchUsed()
    resetMerchant()
  }

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.MANAGE_CHANNEL, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
    })
  }, [])

  const isLoading = isChannelsFetching || isCurrenciesLoading
  const hasActiveFilters = merchant !== 'ALL' || search?.length

  const handleChangePage = (newPage: number) => {
    setPage(newPage)
    const newOffset = newPage * Number(rowsPerPage)
    setOffset(newOffset)
  }

  const handleChangeRowsPerPage = (newRowsPerPage: RowsPerPageType) => {
    setRowsPerPage(newRowsPerPage)
    setPage(0)
    setOffset(0)
  }

  const handleContactSelect = (contact: Contact, channel: Channel) => {
    const requiredFields = getRequiredContactFieldsForCryptoAddress(contact)
    if (requiredFields.length) {
      setContactAction({
        actionId: ContactActionIds.CONTACT,
        header: t('contacts.updateContact'),
        onClose: () => {
          queryClient.invalidateQueries({ queryKey: ['contacts'] })
          queryClient.refetchQueries({ queryKey: ['channels'] })
          setContact(null)
          setIsContactSheetOpen(false)
        },
        meta: {
          keepUpdatedContact: true,
          flowType: InstrumentType.CRYPTO,
          contactRequiredFields: {
            requiredFields,
            calloutMessage: t('contacts.contactRequiredAdditionalContactDetails'),
          },
        },
      })
      setIsContactSheetOpen(true)
      return
    }

    const data: UpdateChannel = {
      uuid: channel?.uuid,
      customerId: contact?.id,
      displayCurrency: channel?.displayCurrency,
      payCurrency: channel?.payCurrency,
      reference: channel?.reference,
      merchantId: channel?.merchantId,
      complianceDetails: getContactComplianceDetails(contact, PartyDetailsType.ORIGINATOR),
    }
    mutate(data, {
      onSuccess: () => {
        refetchChannels()
        setIsSheetOpen(false)
        toast({
          header: t('payments.channelPayments.contactAssigned', { contactName: contact?.name }),
          variant: 'success',
        })
      },
      onError: (error: BaseErrorResponse) => {
        logSentryErrorAndGetEventId(
          `Failed API request to /v2/channel with status ${error?.status} - updateChannel`,
          error,
          {
            data,
          }
        )
        toast({
          header: t('contacts.contactCouldNotBeAssigned'),
          body: t('contacts.contactAssignTechnicalError'),
          variant: 'error',
        })
      },
    })
  }

  const handleOpenChannelPaymentSheet = (channel: Channel) => {
    setWalletAction({
      actionId: ActionIds?.CONTACT_ASSIGN,
      header: t('payments.channelPayments.contactStepHeader'),
      onClose: () => {
        setIsSheetOpen(false)
      },
      meta: {
        handleContactSelect: (contact: Contact) => handleContactSelect(contact, channel),
      },
    })
    setIsSheetOpen(true)
  }

  const handleDeleteChannelClick = (channel: Channel) => {
    setSelectedChannelToDelete(channel)
  }

  const handleCloseDeleteModal = () => {
    setSelectedChannelToDelete(null)
  }

  const noData = !search && !channels?.length && merchant == 'ALL'

  return (
    <Wrapper className="channels-container">
      {(!noData || isLoading) && (
        <PageHeader
          description={t('manageChannelsIntroText')}
          {...{
            renderRightSection: (
              <ChannelsHeaderButtons refetchChannels={refetchChannels} showManageChannels={false} />
            ),
          }}
        />
      )}

      {(!noData || isLoading) && <ChannelsFilterBar />}

      {noData && isLoading && <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />}

      {noData && !isLoading && (
        <EmptyState
          testId="empty-state"
          LottiePlayer={<Lottie src="no-data" />}
          title={t('noChannelsFound')}
          description={
            <>
              {t('payments.channelPayments.noChannelsFound')}{' '}
              <ExternalLink href={process.env.CHANNELS_LEARN_MORE}>{t('learnMore')}</ExternalLink>
            </>
          }
          PrimaryButton={
            <ChannelsHeaderButtons
              refetchChannels={refetchChannels}
              showManageChannels={false}
              createButtonCopy={t('payments.addChannel')}
            />
          }
        />
      )}

      {!noData && (
        <>
          {isChannelsError && (
            <EmptyState
              testId="empty-state"
              LottiePlayer={<Lottie src="connection-error" />}
              title={t('defaultError')}
            />
          )}

          {!isLoading && !channels?.length && hasActiveFilters && !isChannelsError && (
            <EmptyState
              testId="empty-state"
              LottiePlayer={<Lottie src="no-results" />}
              title={t('noChannelsFoundFiltered')}
              PrimaryButton={<Button onClick={onResetAll}>{t('resetAllFilters')}</Button>}
            />
          )}

          {!isLoading && !!channels?.length && !isChannelsError && (
            <>
              <ChannelsTable
                channels={channels}
                currencies={currencies}
                handleOpenChannelPaymentSheet={handleOpenChannelPaymentSheet}
                setIsSheetOpen={setIsSheetOpen}
                setWalletAction={setWalletAction}
                refetchChannels={refetchChannels}
                onDeleteChannelClick={handleDeleteChannelClick}
              />
              {paging?.total > 10 && (
                <Pagination
                  testId="manage-channels-pagination"
                  page={page}
                  rowsPerPage={rowsPerPage}
                  total={paging?.total ?? 0}
                  onChangePage={handleChangePage}
                  onChangeRowsPerPage={handleChangeRowsPerPage}
                />
              )}
            </>
          )}
        </>
      )}
      <WalletSheet isSheetOpen={isSheetOpen} />
      {isPendingUpdateChannel && <ContactsSearchListLoader />}
      <ContactsSheets isSheetOpen={isContactSheetOpen} isNestedSheet={true} />
      {selectedChannelToDelete && (
        <ChannelDelete
          channelToDelete={selectedChannelToDelete}
          handleCloseModal={handleCloseDeleteModal}
          onDeleteCallback={refetchChannels}
        />
      )}
    </Wrapper>
  )
}

export default React.memo(ManageChannels)
